import React, { useMemo } from "react";
import { Alert, Box, Chip, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import PlanCard from "./PlanCard";
import type { BillingInterval, LicensingPlanDto, PendingPlanChange } from '../../models/License';

type SubscriptionPlansProps = {
    plans: LicensingPlanDto[];
    currentTierCode?: string;
    /** Billing period of the current subscription; null/undefined for TRIAL or manual plans. */
    currentInterval?: BillingInterval | null;
    pendingChange?: PendingPlanChange | null;
    interval: BillingInterval;
    onIntervalChange: (interval: BillingInterval) => void;
    alreadyUsedTrail: boolean;
    loading?: boolean;
    onSelectPlan: (plan: LicensingPlanDto) => void;
};

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
    plans,
    currentTierCode,
    currentInterval,
    pendingChange,
    interval,
    onIntervalChange,
    alreadyUsedTrail,
    loading = false,
    onSelectPlan,
}) => {
    const orderedPlans = useMemo(() => {
        const rank: Record<string, number> = {
            TRIAL: 0,
            BASIC: 1,
            ADVANCED: 2,
            PREMIUM: 3,
        };

        return [...plans].sort(
            (a, b) => (rank[a.tierCode.toUpperCase()] ?? 99) - (rank[b.tierCode.toUpperCase()] ?? 99)
        );
    }, [plans]);

    // The licence may report its tier as the code ("BASIC"), the tier name ("Collection") or the plan name,
    // depending on the endpoint, so match any of them (case-insensitive, trimmed).
    const currentTier = (currentTierCode ?? "").trim().toUpperCase();
    const isSameTier = (plan: LicensingPlanDto) =>
        currentTier !== "" &&
        [plan.tierCode, plan.tierName, plan.planName].some(v => (v ?? "").trim().toUpperCase() === currentTier);

    const currentIndex = orderedPlans.findIndex(isSameTier);

    // Current = same tier and, for paid subscriptions, the same billing period as the toggle.
    const isCurrentPlan = (plan: LicensingPlanDto) =>
        isSameTier(plan) &&
        (!currentInterval || plan.tierCode.toUpperCase() === "TRIAL" || currentInterval === interval);

    const pendingFor = (plan: LicensingPlanDto) =>
        pendingChange &&
            pendingChange.licensingPlanId === plan.licensingPlanId &&
            pendingChange.billingInterval === interval
            ? pendingChange.effectiveAtUtc
            : null;

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
            <Box mb={2} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2, width: "100%" }}>
                <Box>
                    <Typography variant="h6">Plans</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Compare subscription options and upgrade when you need more capacity.
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={interval}
                        onChange={(_, value: BillingInterval | null) => value && onIntervalChange(value)}
                        aria-label="Billing period"
                    >
                        <ToggleButton value="month">Monthly</ToggleButton>
                        <ToggleButton value="year">Annual</ToggleButton>
                    </ToggleButtonGroup>
                    <Chip label="Annual: 2 months free" color="success" size="small" variant="outlined" />
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
                Upgrades apply immediately and you only pay the difference for the rest of your billing period.
                Downgrades, switching from annual to monthly, and cancellations take effect at your next renewal.
                Paid periods aren't refunded.
            </Alert>

            {/* Equal-width columns across the full page width; badges sit on the top edge, so leave room above. */}
            <Box
                sx={{
                    display: 'grid',
                    width: '100%',
                    gap: 2,
                    pt: 1.5,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        md: 'repeat(3, minmax(0, 1fr))',
                        lg: `repeat(${Math.max(1, Math.min(orderedPlans.length, 5))}, minmax(0, 1fr))`,
                    },
                }}
            >
                {orderedPlans.map((plan, index) => (
                    <Box key={plan.licensingPlanId} sx={{ display: 'flex' }}>
                        <PlanCard
                            plan={plan}
                            interval={interval}
                            isCurrent={isCurrentPlan(plan)}
                            isAvailable={plan.tierCode.toUpperCase() === "TRIAL" ? !alreadyUsedTrail : true}
                            isRecommended={currentIndex >= 0 && index === currentIndex + 1}
                            pendingAtUtc={pendingFor(plan)}
                            loading={loading}
                            onSelect={onSelectPlan}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default SubscriptionPlans;
